// ADO-1779 — the mobile browse side tray (Figma Browse — Layer 1 · All
// Departments 4002:17414, Layer 2 · Bread & Cakes 4002:17492, Layer 3 · Bakery
// 4002:17554, Mobile 320).
//
// On mobile the three side-by-side mega-menu columns become ONE 280px tray that
// shows a single layer at a time: tapping a node with children replaces the
// tray's contents with that node's children (the back row names the layer it
// returns to), and tapping a leaf commits the selection. The owner decides what
// a commit does (drill the listing on /products, hand over to /products
// elsewhere) and closes the tray, exactly as with the desktop BrowseNav.
//
// Portalled to <body>: the tray covers the whole header (Figma draws it from
// the top of the viewport over the blue bar), which the sub-nav's stacking
// context — where the owner renders the overlay — cannot paint above. The
// container still carries the overlay id, so SiteHeader's outside-click
// dismissal keeps treating taps inside it (including the scrim, which closes it
// itself) as inside the menu.
//
// The scrim blocks the page behind it, so the tray is a modal dialog (the
// FiltersDrawer precedent) holding the department navigation: the shared
// useFocusTrap moves focus in on open, wraps Tab / Shift+Tab inside the tray and
// hands focus back to the opener when it closes. Escape is the owner's, as for
// the desktop menu. The layer heading takes focus on open and on every drill
// (the tapped row unmounts), so a screen reader announces the new layer.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CategoryNode } from '../../api/browse.client';
import { NoEntryPrompt } from '../../ordering/NoEntryPrompt';
import { Icon, useFocusTrap } from '../../design-system';
import { formatCategoryName } from './categoryDisplay';
import { useCategoryChildren } from './useCategoryChildren';
import styles from './BrowseTray.module.css';

const ROOT_TITLE = 'All Departments';

export interface BrowseTrayProps {
  /** DOM id of the overlay container (the Browse trigger's `aria-controls`). */
  readonly id: string;
  /** A leaf (a node without children) was tapped. */
  readonly onSelectNode: (node: CategoryNode) => void;
  /** A node with children was tapped and its layer is now shown. */
  readonly onExpandNode: (node: CategoryNode) => void;
  /** The close control or the scrim was tapped. */
  readonly onClose: () => void;
}

export function BrowseTray({ id, onSelectNode, onExpandNode, onClose }: BrowseTrayProps) {
  // The drilled path; empty = the All Departments layer.
  const [trail, setTrail] = useState<readonly CategoryNode[]>([]);
  const current = trail.at(-1) ?? null;
  const layer = useCategoryChildren(current?.id ?? null, current?.name ?? ROOT_TITLE, true);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Declared before the heading effect below, so on open the trap captures the
  // opener first and the heading then takes focus from the trap's first control.
  const trapTab = useFocusTrap(true, dialogRef);

  // The layer heading takes focus on open and on every drill / back.
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [trail]);

  const drillInto = (node: CategoryNode) => {
    onExpandNode(node);
    setTrail((path) => [...path, node]);
  };
  const back = () => setTrail((path) => path.slice(0, -1));

  const title = current ? formatCategoryName(current.name) : ROOT_TITLE;
  const parent = trail.at(-2);
  const backLabel = parent ? formatCategoryName(parent.name) : ROOT_TITLE;

  return createPortal(
    <div id={id} className={styles.root}>
      <div className={styles.scrim} data-testid="browse-tray-scrim" aria-hidden onClick={onClose} />
      {/* The onKeyDown is the dialog's Tab trap (WAI-ARIA dialog pattern), as in
          FiltersDrawer. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Browse"
        tabIndex={-1}
        className={styles.dialog}
        onKeyDown={trapTab}
      >
        <nav className={styles.tray} aria-label="Browse departments">
          <div className={styles.header}>
            {current && (
              <button
                type="button"
                className={styles.back}
                onClick={back}
                aria-label={`Back to ${backLabel}`}
              >
                <Icon name="caretLeft" size={16} />
                <span className={styles.backLabel}>{backLabel}</span>
              </button>
            )}
            <div className={styles.titleRow} data-drilled={current ? 'true' : undefined}>
              <h2 ref={headingRef} tabIndex={-1} className={styles.title}>
                {title}
              </h2>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close browse menu"
              >
                <Icon name="x" size={20} />
              </button>
            </div>
          </div>

          {layer.noEntry && <NoEntryPrompt className={styles.hint} />}
          {layer.loading && (
            <p className={styles.hint} role="status">
              Loading…
            </p>
          )}
          {layer.error && (
            <p className={styles.hint} role="alert">
              {layer.error}
            </p>
          )}
          {!layer.loading && !layer.error && !layer.noEntry && layer.nodes.length === 0 && (
            <p className={styles.hint}>Nothing here.</p>
          )}
          <ul className={styles.list}>
            {layer.nodes.map((node) => (
              <li key={node.id} className={styles.item}>
                <button
                  type="button"
                  className={styles.row}
                  onClick={() => (node.hasChildren ? drillInto(node) : onSelectNode(node))}
                >
                  <span className={styles.rowLabel}>{formatCategoryName(node.name)}</span>
                  {/* Figma draws the chevron only where there is a layer to open. */}
                  {node.hasChildren && (
                    <Icon name="caretRight" size={16} className={styles.chevron} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>,
    document.body,
  );
}
